import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Either a bare string (used as both label and value) or an explicit pair. */
export type SelectInputOption = string | { label: string; value: string };

export interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Floating label rendered above the select. */
  label?: ReactNode;
  /** Options the dropdown lists. Strings get expanded to `{label,value}`. */
  options: SelectInputOption[];
  /** Disabled placeholder option shown when value is empty. */
  placeholder?: string;
  /** When true, the select + helper render in red. */
  error?: boolean;
  /** Replaces helperText when `error` is true. */
  errorMessage?: ReactNode;
  /** Helper hint shown below the select when there is no error. */
  helperText?: ReactNode;
}

/**
 * Floating-label native `<select>`. Lifted from My.Tms.Old/components/SelectInput.tsx
 * with the `onDelete` knob trimmed (rare; can be reintroduced if a screen
 * needs it). Visual matches `<TextInput>` so they line up inside FormSection
 * grids.
 */
export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(function SelectInput(
  { label, options, placeholder, error = false, errorMessage, helperText, id, className, ...props },
  ref,
) {
  const reactId = useId();
  const selectId = id ?? props.name ?? reactId;
  const norm = options.map((o) => (typeof o === 'string' ? { label: o, value: o } : o));

  return (
    <div className="w-full">
      <div className="relative">
        <select
          id={selectId}
          ref={ref}
          className={cn(
            'peer h-9 w-full rounded-sm border bg-white px-2 pb-1 pt-2 text-sm font-semibold text-slate-900',
            'focus:outline-none focus:ring-2',
            // Soft disabled look — `bg-slate-50` instead of the default
            // browser gray so the floating label sits comfortably against
            // the field. Mirrors the same treatment on TextInput.
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
            error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500',
            className,
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}
          {norm.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500',
              error && 'peer-focus:text-red-500',
            )}
          >
            {label}
          </label>
        )}
      </div>
      {(helperText || (error && errorMessage)) && (
        <p className={cn('mt-1 text-xs', error ? 'text-red-600' : 'text-slate-500')}>
          {error ? errorMessage : helperText}
        </p>
      )}
    </div>
  );
});
